// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    return (
        <>
            <DataTableCell {...styles.dataCell}>
                <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <SmallHeadingAndText heading="" text={quote.typeDisplay} extendedStyles={styles.typeHeadingAndText} />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <SmallHeadingAndText
                    heading=""
                    text={quote.statusDisplay}
                    extendedStyles={styles.statusHeadingAndText}
                />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <SmallHeadingAndText
                    heading=""
                    text={orderDateDisplay}
                    extendedStyles={styles.orderDateHeadingAndText}
                />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {expirationDateDisplay && (
                    <SmallHeadingAndText
                        heading=""
                        text={expirationDateDisplay}
                        extendedStyles={styles.expirationDateHeadingAndText}
                    />
                )}
            </DataTableCell>
            {session.isSalesPerson && (
                <DataTableCell {...styles.dataCell}>
                    <>
                        <SmallHeadingAndText
                            heading=""
                            text={quote.salespersonName}
                            extendedStyles={styles.salesRepHeadingAndText}
                        />

                        <SmallHeadingAndText
                            heading=""
                            text={quote.userName}
                            extendedStyles={styles.userNameHeadingAndText}
                        />
                    </>
                </DataTableCell>
            )}
            <DataTableCell {...styles.dataCell}>
                <SmallHeadingAndText heading="" text={customerDisplay} extendedStyles={styles.customerHeadingAndText} />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {!session.isSalesPerson && (
                    <SmallHeadingAndText
                        heading=""
                        text={quote.shipToFullAddress}
                        extendedStyles={styles.shipToAddressHeadingAndText}
                    />
                )}
            </DataTableCell>
        </>
    );
};

export default QuoteSummaryCard;
